import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, trim } from "lodash";

import ITRFilingApi from "../../api/ITRFilingApi";
import PlansApi from "../../api/PlansApi";
import { AppAuthRoles } from "../../config/app-constants";
import {
  EToastvariant,
  IApiState,
  IAssignedExpert,
  IEfilingPlan,
  IFilingResponse,
  IFormITR1,
  ILoggedInUserRoot,
  IPlansRoot
} from "../../config/interfaces";
import { UtilService } from "../../services";
import { RootState } from "../Store";
import { showToastMsg } from "./ToastMsgSlice";

interface ITRFileState {
	currentFilingData: IFilingResponse | null;
	currentFilingExpertData: IAssignedExpert | null;
	currentFilingPlan: IEfilingPlan | null;
	currentFilingUUID: string | null;
	isFilingReadOnly: boolean;
	RFLoadingStatus: IApiState; // Loader for recent filing details
	CFLoadingStatus: IApiState; // Loader for current filing details
	PlansLoadingStatus: IApiState; // Loader for plans list fetching
	plansData: IPlansRoot[];
	checkOutPlan: IPlansRoot | null;
	selfPlansList: IPlansRoot[];
	assistedPlansList: IPlansRoot[];
}

const initialState: ITRFileState = {
	currentFilingData: null,
	currentFilingExpertData: null,
	currentFilingPlan: null,
	currentFilingUUID: null,
	isFilingReadOnly: false,
	RFLoadingStatus: "PENDING",
	CFLoadingStatus: "PENDING",
	PlansLoadingStatus: "PENDING",
	plansData: [],
	checkOutPlan: null,
	selfPlansList: [],
	assistedPlansList: []
};

const ITRFilingSlice = createSlice({
	name: "ITRFilingSlice",
	initialState,
	reducers: {
		setCurrentFilingUUID: (state, action: PayloadAction<string>) => {
			state.currentFilingUUID = action.payload;
		},
		unsetCurrentFilingUUID: (state) => {
			state.currentFilingUUID = null;
		},
		setCurrentFilingData: (
			state,
			{ payload }: PayloadAction<{ filingData: IFilingResponse; loggedInUser: ILoggedInUserRoot }>
		) => {
			state.currentFilingData = cloneDeep(payload.filingData);
			const { loggedInUser, filingData } = payload;
			const { completedAt, efilingPlan, userUuid } = filingData;

			if (loggedInUser?.role === AppAuthRoles.ADMIN || completedAt) {
				state.isFilingReadOnly = true;
			} else if (
				efilingPlan &&
				!efilingPlan?.inactive &&
				efilingPlan?.planData?.type === "assisted" &&
				loggedInUser?.uuid === userUuid
			) {
				state.isFilingReadOnly = true;
			} else {
				state.isFilingReadOnly = false;
			}
		},
		unsetCurrentFilingData: (state) => {
			state.currentFilingData = null;
		},
		setCurrentFilingExpertData: (state, action: PayloadAction<IAssignedExpert>) => {
			state.currentFilingExpertData = cloneDeep(action.payload);
		},
		unsetCurrentFilingExpertData: (state) => {
			state.currentFilingExpertData = null;
		},
		setCurrentFilingPlan: (state, action: PayloadAction<IEfilingPlan>) => {
			state.currentFilingPlan = cloneDeep(action.payload);
		},
		unsetCurrentFilingPlan: (state) => {
			state.currentFilingPlan = null;
		},
		setCheckOutPlan: (state, action: PayloadAction<IPlansRoot>) => {
			state.checkOutPlan = cloneDeep(action.payload);
		},
		unsetCheckOutPlan: (state) => {
			state.checkOutPlan = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchRecentEfiling.pending, (state) => {
			state.RFLoadingStatus = "PENDING";
		});
		builder.addCase(
			fetchRecentEfiling.fulfilled,
			(state, { payload }: PayloadAction<IFilingResponse[]>) => {
				if (payload?.length > 0) {
					state.currentFilingData = payload[0];
					state.currentFilingUUID = payload[0].uuid;
				} else {
					state.currentFilingData = null;
					state.currentFilingUUID = null;
				}
				state.RFLoadingStatus = "FULFILLED";
			}
		);
		builder.addCase(fetchRecentEfiling.rejected, (state) => {
			state.RFLoadingStatus = "REJECTED";
		});

		builder.addCase(getFilingDetails.pending, (state) => {
			state.CFLoadingStatus = "PENDING";
		});
		builder.addCase(getFilingDetails.fulfilled, (state, { payload }: PayloadAction<any>) => {
			if (payload) {
				state.currentFilingData = payload;
				state.currentFilingUUID = payload.uuid;
				state.currentFilingPlan = payload?.efilingPlan || null;
				state.currentFilingExpertData = payload?.efilingAuthorizedUser || null;
			} else {
				state.currentFilingData = null;
				state.currentFilingUUID = null;
				state.currentFilingPlan = null;
				state.currentFilingExpertData = null;
			}

			state.CFLoadingStatus = "FULFILLED";
		});
		builder.addCase(getFilingDetails.rejected, (state) => {
			state.CFLoadingStatus = "REJECTED";
		});

		builder.addCase(getPlanList.pending, (state) => {
			state.PlansLoadingStatus = "PENDING";
		});
		builder.addCase(getPlanList.fulfilled, (state, { payload }: PayloadAction<IPlansRoot[]>) => {
			if (payload?.length) {
				state.plansData = payload;
				state.selfPlansList = payload?.filter((plan) => plan.type === "self") || [];
				state.assistedPlansList = payload?.filter((plan) => plan.type === "assisted") || [];
			} else {
				state.plansData = [];
			}

			state.PlansLoadingStatus = "FULFILLED";
		});
		builder.addCase(getPlanList.rejected, (state) => {
			state.PlansLoadingStatus = "REJECTED";
		});
	}
});

export const fetchRecentEfiling = createAsyncThunk<IFilingResponse[]>(
	"ITRFiling/fetchRecentEfiling",
	async (_, { rejectWithValue }) => {
		try {
			const api = ITRFilingApi.getInstance();
			const filingResp = await api.getITRFilings({});
			return filingResp.records;
		} catch (error: any) {
			console.log(UtilService.getErrorMessage(error));
			return rejectWithValue(error.response.data);
		}
	}
);

export const getFilingDetails = createAsyncThunk(
	"ITRFiling/getFilingDetails",
	async (filingUUID: string | undefined, { dispatch, getState, rejectWithValue }) => {
		try {
			const { loggedInUserReducer } = getState() as RootState;
			const loggedInUser = loggedInUserReducer?.loggedInUser;
			if (!filingUUID || !loggedInUser) {
				return;
			}
			const filingApi = ITRFilingApi.getInstance();
			const filingResp = await filingApi.getFilingByUuId(filingUUID);

			dispatch(
				setCurrentFilingData({
					filingData: filingResp,
					loggedInUser: loggedInUser
				})
			);
			return filingResp;
		} catch (error: any) {
			console.log(UtilService.getErrorMessage(error));
			return rejectWithValue(error.response.data);
		}
	}
);

export const getPlanList = createAsyncThunk<IPlansRoot[]>(
	"ITRFiling/getPlanPurchase",
	async (_, { rejectWithValue }) => {
		try {
			const api = PlansApi.getInstance();
			const { records } = await api.getPlans({});
			return records;
		} catch (error: any) {
			console.log(UtilService.getErrorMessage(error));
			return rejectWithValue(error.response.data);
		}
	}
);

export const autoSaveChanges = createAsyncThunk(
	"ITRFiling/autoSaveChanges",
	async (data: { formData: IFormITR1; filingUUID: string }, { dispatch, getState }) => {
		const { formData, filingUUID } = data;

		try {
			const fullname = trim(
				`${formData?.FirstName ? `${formData.FirstName} ` : ""}${
					formData?.MiddleName ? `${formData.MiddleName} ` : ""
				}${formData.SurNameOrOrgName}`
			);
			const filingData = {
				efilingData: formData,
				name: fullname,
				pan: formData.PAN,
				fileUsedBy: formData.Status,
				assessmentYear: "2024-25"
			};

			let responseData: IFilingResponse | null = null;
			const filingApi = ITRFilingApi.getInstance();
			responseData = await filingApi.updateITRFiling(filingUUID, filingData);
			dispatch(setCurrentFilingUUID(responseData.uuid));
			const { loggedInUserReducer } = getState() as RootState;
			const loggedInUser = loggedInUserReducer?.loggedInUser;
			if (!loggedInUser) {
				return;
			}
			dispatch(
				setCurrentFilingData({
					filingData: responseData,
					loggedInUser: loggedInUser
				})
			);
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Saved",
					message: "Information saved successfully",
					variant: EToastvariant.Success
				})
			);
		} catch (error: any) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		}
	}
);

const getPlanAmount = (plansData: IPlansRoot[], type: "max" | "min"): number | null => {
	if (!plansData.length) {
		return null;
	}

	const plansAmounts = plansData
		.map((record) => Number(record.pricingTitle))
		.filter((amount) => !isNaN(amount));

	if (!plansAmounts.length) {
		return null;
	}

	return type === "max" ? Math.max(...plansAmounts) : Math.min(...plansAmounts);
};

export const {
	setCurrentFilingUUID,
	unsetCurrentFilingUUID,
	setCurrentFilingData,
	unsetCurrentFilingData,
	setCurrentFilingExpertData,
	unsetCurrentFilingExpertData,
	setCurrentFilingPlan,
	unsetCurrentFilingPlan,
	setCheckOutPlan,
	unsetCheckOutPlan
} = ITRFilingSlice.actions;

export const ITRFilingSelector = (rootState: RootState) => rootState.ITRFilingReducer;

export const MaxPlansAmountSelector = (rootState: RootState): number | null => {
	return getPlanAmount(rootState.ITRFilingReducer.plansData, "max");
};

export const MinPlansAmountSelector = (rootState: RootState): number | null => {
	return getPlanAmount(rootState.ITRFilingReducer.plansData, "min");
};

export default ITRFilingSlice.reducer;
